import { Box, Typography } from "@mui/material";

import archFrame from "../../../assets/Images/Temples/m-arch-frame.svg";
import mandala from "../../../assets/Images/Temples/m-mandala.svg";
import checkIcon from "../../../assets/Images/Temples/m-check.svg";
import mBell0 from "../../../assets/Images/Temples/m-bell-0.svg";
import mBell1 from "../../../assets/Images/Temples/m-bell-1.svg";
import mBell2 from "../../../assets/Images/Temples/m-bell-2.svg";
import { swing } from "./TempleArch";
import { float, spin } from "./motion";
import { HINDI_FONT, TEMPLE_COLORS, TempleButton } from "../shared";

// Mobile arch is drawn on a 376px-wide frame; it stretches to the screen
// width, so bells are placed by percentage across it.
const FRAME_WIDTH = 376;
const FRAME_HEIGHT = 743;
const BELL_WIDTH = 20;
const BELLS = [
  { src: mBell0, x: 45.5, y: 73.6, h: 38, angle: -8 },
  { src: mBell1, x: 120.1, y: 42.3, h: 46, angle: -5 },
  { src: mBell2, x: 188.5, y: 25.9, h: 42, angle: -6.5 },
  { src: mBell1, x: 256.9, y: 42.2, h: 46, angle: -8 },
  { src: mBell0, x: 331.5, y: 73.7, h: 38, angle: -5 },
];

const reducedMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

const MobileButtonSx = { flex: 1, minWidth: 0, fontSize: "14px", py: "12px", px: "10px", boxShadow: "none" };

export const MobileStats = ({ stats }) => (
  <Box sx={{ background: "#FFFFFF", px: "20px", py: "32px" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        border: "1px solid #EFE4D3",
        borderRadius: "16px",
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {stats.map((stat, index) => (
        <Box
          key={stat.label}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            px: "16px",
            py: "18px",
            textAlign: "center",
            borderRight: index % 2 === 0 ? "1px solid #EFE4D3" : "none",
            borderBottom: index < stats.length - (stats.length % 2 || 2) ? "1px solid #EFE4D3" : "none",
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "19px",
              background: TEMPLE_COLORS.blush,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box component="img" src={stat.icon} alt="" sx={{ maxWidth: 18, maxHeight: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "22px", lineHeight: "28px", color: TEMPLE_COLORS.ink }}>
            {stat.value}
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: TEMPLE_COLORS.muted }}>
            {stat.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const MobileHero = ({ hero }) => (
  <Box sx={{ px: "7px" }}>
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "12px",
        minHeight: FRAME_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "10px",
        pt: "110px",
        px: "28px",
        pb: "10px",
        // Own stacking context so the mandala (z-index -1) sits under the arch frame.
        isolation: "isolate",
      }}
    >
      <Box
        component="img"
        src={archFrame}
        alt=""
        sx={{ position: "absolute", left: 0, top: 0, width: "100%", height: FRAME_HEIGHT, pointerEvents: "none" }}
      />
      {/* The arch keeps its design height so the bells stay on the scallop
          points. When longer text makes the hero taller, a second copy pinned
          to the bottom shows only its lower half (plain side bands) to extend
          the frame down. */}
      <Box
        component="img"
        src={archFrame}
        alt=""
        sx={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          height: FRAME_HEIGHT,
          clipPath: "inset(50% 0 0 0)",
          pointerEvents: "none",
        }}
      />
      {BELLS.map((bell, index) => (
        <Box
          key={index}
          component="img"
          src={bell.src}
          alt=""
          sx={{
            position: "absolute",
            left: `calc(${((bell.x / FRAME_WIDTH) * 100).toFixed(3)}% - ${BELL_WIDTH / 2}px)`,
            top: bell.y,
            width: BELL_WIDTH,
            height: bell.h,
            pointerEvents: "none",
            "--bell-rest": `${bell.angle}deg`,
            transform: "rotate(var(--bell-rest))",
            transformOrigin: "50% 0",
            animation: `${swing} ${2.4 + (index % 3) * 0.3}s ease-in-out ${-index * 0.35}s infinite`,
            ...reducedMotion,
          }}
        />
      ))}

      <Typography
        sx={{
          position: "relative",
          width: "100%",
          textAlign: "center",
          fontFamily: HINDI_FONT,
          fontSize: "14px",
          lineHeight: "20px",
          color: TEMPLE_COLORS.red,
        }}
      >
        {hero.tagline}
      </Typography>
      <Typography component="h1" sx={{ position: "relative", fontWeight: 800, fontSize: "32px", lineHeight: "35px", color: TEMPLE_COLORS.ink }}>
        {hero.titleLine1}
        <Box component="span" sx={{ display: "block", color: TEMPLE_COLORS.red }}>
          {hero.titleLine2}
        </Box>
      </Typography>
      <Typography sx={{ position: "relative", fontSize: "12px", lineHeight: "18px", color: TEMPLE_COLORS.body }}>
        {hero.description}
      </Typography>

      <Box sx={{ position: "relative", display: "flex", gap: "10px", width: "100%" }}>
        <TempleButton link={hero.primaryCta.link} sx={MobileButtonSx}>
          {hero.primaryCta.label}
        </TempleButton>
        <TempleButton link={hero.secondaryCta.link} outlined sx={{ ...MobileButtonSx, borderColor: "rgba(29, 29, 27, 0.3)" }}>
          {hero.secondaryCta.label}
        </TempleButton>
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexWrap: "wrap",
          alignContent: "flex-start",
          columnGap: "14px",
          rowGap: "6px",
          width: "100%",
          minHeight: 70, // fixed block height in the design, so the photo lands where Figma puts it
        }}
      >
        {hero.trustPoints.map((point) => (
          <Box key={point} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box component="img" src={checkIcon} alt="" sx={{ width: 16, height: 16 }} />
            <Typography sx={{ fontWeight: 500, fontSize: "12px", lineHeight: "18px", color: TEMPLE_COLORS.ink }}>
              {point}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Gold-outlined arch window with the floating badge */}
      <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
        {/* Mandala as in the design: 432.9px, centred on the photo, its top
            48px above the photo's top; z-index -1 keeps it under the frame. */}
        <Box
          component="img"
          src={mandala}
          alt=""
          sx={{
            position: "absolute",
            left: "calc(50% - 216.45px)",
            top: -48,
            width: 432.9,
            height: 432.9,
            maxWidth: "none", // wider than the hero; the global img max-width would squash it into an oval
            zIndex: -1,
            opacity: 0.5,
            pointerEvents: "none",
            animation: `${spin} 90s linear infinite`,
            ...reducedMotion,
          }}
        />
        <Box sx={{ position: "relative", width: 270, height: 324 }}>
          <Box sx={{ position: "absolute", inset: 0, border: "1.08px solid #C9952B", borderRadius: "135px 135px 14.4px 14.4px" }} />
          <Box
            component="img"
            src={hero.image}
            alt=""
            sx={{
              position: "absolute",
              left: 10.8,
              top: 10.8,
              width: 248.4,
              height: 302.4,
              objectFit: "cover",
              borderRadius: "124.2px 124.2px 9px 9px",
            }}
          />
          {hero.badge && (
            <Box
              sx={{
                position: "absolute",
                left: -19,
                top: 242,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.4px",
                p: "13.889px",
                background: "#FFFFFF",
                borderRadius: "11.111px",
                boxShadow: "0px 8.333px 20.833px 0px rgba(77, 26, 0, 0.15)",
                whiteSpace: "nowrap",
                animation: `${float} 4s ease-in-out infinite`,
                ...reducedMotion,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "25px", lineHeight: "27.778px", color: TEMPLE_COLORS.ink }}>
                {hero.badge.value}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: "9.03px", lineHeight: "12.5px", color: TEMPLE_COLORS.muted }}>
                {hero.badge.label}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  </Box>
);

export default MobileHero;
